/**
 * useServiceQueueColumn - Hook para carregar itens de uma coluna específica da Fila de Atendimento
 *
 * CHANGELOG:
 *   v2.0.0 - 2026-06-26 — Removida a subscription Realtime por coluna
 *            (2 canais × 5 colunas = 10). A assinatura passa a ser
 *            centralizada em useServiceQueueRealtime (1 canal por aba),
 *            que faz patch otimista no cache desta query e invalidações
 *            debounced. Hook fica somente como data-fetching/paginação.
 *   v1.0.0 - 2026-01-30 — Versão inicial.
 *
 * Implementa cursor-based pagination com infinite scroll por coluna,
 * seguindo o padrão do useKanbanConversationColumn.ts
 */

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { QueueSituation, QueueItem, QueueFilters } from './useServiceQueue';

// Logger para depuração condicional
const logger = createLogger('ServiceQueueColumn');

// ============================================================================
// Constantes
// ============================================================================
const PAGE_SIZE = 25;
const STALE_TIME = 30 * 1000; // 30 segundos

// ============================================================================
// Tipos internos
// ============================================================================

// Tipo do retorno da RPC (tipagem interna)
interface RpcQueueItem {
  id: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_avatar_url: string | null;
  inbox_id: string;
  inbox_name: string;
  inbox_identifier: string;
  // v1.4.0: canal/provedor para badge no card do Kanban
  inbox_channel_type: string | null;
  inbox_provider_slug: string | null;
  department_id: string | null;
  department_name: string | null;
  assigned_agent_id: string | null;
  assigned_agent_name: string | null;
  assigned_agent_avatar: string | null;  // v1.2.0
  status: string;
  situation: string;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  has_pending_transfer: boolean;
  transfer_id: string | null;
  transfer_to_agent_id: string | null;
  transfer_to_agent_name: string | null;
  transfer_to_department_id: string | null;
  transfer_to_department_name: string | null;
  transfer_from_agent_id: string | null;
  transfer_from_agent_name: string | null;
  transfer_created_at: string | null;
  // v1.3.0: dados do grupo
  group_id: string | null;
  group_name: string | null;
  group_avatar_url: string | null;
}

// Tipo do cursor de paginação
interface PageCursor {
  cursor: string | null;
  id: string;
}

// ============================================================================
// Funções auxiliares
// ============================================================================

/**
 * Calcula tempo de espera em segundos desde a criação
 */
function calculateWaitingTime(createdAt: string): number {
  const created = new Date(createdAt);
  const now = new Date();
  return Math.floor((now.getTime() - created.getTime()) / 1000);
}

/**
 * Mapeia retorno da RPC para QueueItem
 */
function mapRpcToQueueItem(item: RpcQueueItem): QueueItem {
  return {
    id: item.id,
    contact: item.contact_id ? {
      id: item.contact_id,
      name: item.contact_name,
      phone: item.contact_phone,
      avatar_url: item.contact_avatar_url,
    } : null,
    inbox: {
      id: item.inbox_id,
      name: item.inbox_name,
      identifier: item.inbox_identifier,
      // v1.4.0: propaga canal/provedor para o card do Kanban
      channel_type: item.inbox_channel_type ?? null,
      provider_slug: item.inbox_provider_slug ?? null,
    },
    department: item.department_id ? {
      id: item.department_id,
      name: item.department_name || '',
    } : null,
    assigned_agent: item.assigned_agent_id ? {
      id: item.assigned_agent_id,
      full_name: item.assigned_agent_name,
      avatar_url: item.assigned_agent_avatar,  // v1.2.0
    } : null,
    status: item.status as 'pending' | 'open' | 'closed',
    situation: item.situation as QueueSituation,
    created_at: item.created_at,
    last_message_at: item.last_message_at,
    last_message_preview: item.last_message_preview,
    waiting_time: calculateWaitingTime(item.created_at),
    unread_count: item.unread_count,
    pending_transfer: item.has_pending_transfer && item.transfer_id ? {
      id: item.transfer_id,
      to_agent_id: item.transfer_to_agent_id,
      to_agent_name: item.transfer_to_agent_name,
      to_department_id: item.transfer_to_department_id,
      to_department_name: item.transfer_to_department_name,
      from_agent_id: item.transfer_from_agent_id,
      from_agent_name: item.transfer_from_agent_name,
      created_at: item.transfer_created_at || '',
    } : undefined,
    // v1.3.0: monta objeto de grupo apenas quando a conversa é de grupo
    group: item.group_id ? {
      id: item.group_id,
      name: item.group_name,
      avatar_url: item.group_avatar_url,
    } : null,
  };
}

// ============================================================================
// Hook Principal
// ============================================================================

/**
 * Hook para carregar itens de uma coluna específica da Fila de Atendimento
 * Utiliza cursor-based pagination para performance em escala
 * Permissões aplicadas server-side via RPC
 * 
 * @param situation - Situação da coluna (waiting_bot, waiting_agent, etc)
 * @param filters - Filtros globais (departamento, inbox, agente, busca)
 * @param enabled - Se deve buscar os dados (default: true)
 */
export function useServiceQueueColumn(
  situation: QueueSituation,
  filters?: Partial<QueueFilters>,
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // =========================================================================
  // Query com Paginação Infinita
  // =========================================================================
  const query = useInfiniteQuery({
    // Query key inclui situação e filtros para cache correto por coluna
    queryKey: ['service-queue-column', situation, currentTenant?.id, filters],
    queryFn: async ({ pageParam }) => {
      logger.debug(`Buscando itens da coluna ${situation}, cursor: ${pageParam?.cursor}`);

      // Chama RPC com parâmetros de filtro
      const { data, error } = await supabase.rpc('get_service_queue_items', {
        p_situation: situation, // Filtra por situação específica da coluna
        p_department_id: filters?.departmentId || undefined,
        p_inbox_id: filters?.inboxId || undefined,
        p_agent_id: filters?.agentId || undefined,
        p_search: filters?.search?.trim() || undefined,
        p_limit: PAGE_SIZE,
        p_cursor_last_message_at: pageParam?.cursor || undefined,
        p_cursor_id: pageParam?.id || undefined,
      });

      if (error) {
        logger.error('Erro ao buscar coluna da Fila:', error);
        throw error;
      }

      const items = ((data as RpcQueueItem[]) || []).map(mapRpcToQueueItem);
      logger.debug(`Recebidos ${items.length} itens da coluna ${situation}`);

      return items;
    },
    getNextPageParam: (lastPage): PageCursor | undefined => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return {
        cursor: lastItem.last_message_at,
        id: lastItem.id,
      };
    },
    initialPageParam: null as PageCursor | null,
    enabled: !!currentTenant?.id && enabled,
    staleTime: STALE_TIME,
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  // NOTA: Subscription Realtime foi MOVIDA para useServiceQueueRealtime,
  // que mantém UM ÚNICO canal por aba e aplica patch otimista + debounce
  // (ver CHANGELOG no topo deste arquivo, v2.0.0).

  // =========================================================================
  // Funções de utilidade
  // =========================================================================

  // Função para invalidar cache desta coluna
  const invalidateColumn = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: ['service-queue-column', situation] 
    });
  }, [queryClient, situation]);

  // Flatten todas as páginas em um único array
  const items = useMemo(() => 
    query.data?.pages.flat() || [], 
    [query.data]
  );

  // =========================================================================
  // Retorno
  // =========================================================================
  return {
    items,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateColumn,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook para invalidar todas as colunas da Fila de Atendimento
 * Útil após operações que afetam múltiplas colunas
 */
export function useInvalidateServiceQueueColumns() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    // Invalida todas as colunas
    queryClient.invalidateQueries({
      queryKey: ['service-queue-column'],
    });
    // Também invalida os stats
    queryClient.invalidateQueries({
      queryKey: ['service-queue-stats'],
    });
  }, [queryClient]);
}
